import { LandingDestinationModel } from '@/__generated';
import { PopularDefinition } from '@/components/NtaMap/data/defaultDestination';
import Map from '@/components/NtaMap/map';
import Spin from '@/components/Spin';
import { LocationType } from '@/constants';
import { useDebounce } from '@/hooks/useDebounce';
import { apiSdk } from '@/httpClient';
import hotelService from '@/services/hotelService';
import { faHistory } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Ref, useState } from 'react';
import { UncontrolledDropdown, Dropdown, DropdownToggle, DropdownMenu } from 'reactstrap';

type Props = {
  selectedLocation: LocationSearchSelectedProps | null | undefined;
  elmRef: Ref<{ close: Function; open: Function } | undefined>;
  onChange: (location: LocationSearchSelectedProps | null | undefined, keepOpen?: boolean) => void;
};

export interface LocationSearchSelectedProps {
  name: string;
  code: string;
  type: number;
}
export default function LocationSearch({ elmRef, onChange, selectedLocation }: Props) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const locationRef = React.useRef<any>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [destinations, setDestinations] = React.useState<LandingDestinationModel[]>([]);
  const [searchValue, setSearchValue] = React.useState<string>('');
  const searchValueDebounce = useDebounce(searchValue, 300);

  React.useImperativeHandle(
    elmRef,
    () => {
      return {
        open() {
          setDropdownOpen(true);
        },
        close() {
          setDropdownOpen(false);
        },
      };
    },
    [],
  );

  React.useEffect(() => {
    searchData(searchValueDebounce);
  }, [searchValueDebounce]);

  React.useEffect(() => {
    locationRef.current.value = selectedLocation?.name || '';
    setSearchValue(selectedLocation?.name || '');
  }, [selectedLocation, locationRef]);

  const searchData = (searchString: string) => {
    if (searchString?.trim()) {
      setLoading(true);
      apiSdk.LandingService.searchDestinations({ limit: 20, query: searchString.trim() })
        .then(res => {
          setDestinations(res);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setDestinations([]);
    }
  };

  const onSelectLocation = (name: string, code: string, type: number, keepOpen?: boolean) => {
    onChange(
      name
        ? {
            code: code,
            name: name,
            type: type,
          }
        : undefined,
      keepOpen,
    );
    if (!keepOpen) {
      setDropdownOpen(false);
    }
  };

  const toggle = event => {
    if (event.target.contains(locationRef.current)) {
      if (!dropdownOpen) {
        if (selectedLocation?.name) {
          searchData(selectedLocation.name);
        }
      }
      setDropdownOpen(true);
    } else {
      if (!dropdownOpen) {
        if (selectedLocation?.name) {
          searchData(selectedLocation.name);
        }
      }
      setDropdownOpen(prevState => !prevState);
    }
  };
  const recentSearch = React.useMemo(() => {
    return hotelService.getRecentSearchInfo();
  }, [dropdownOpen]);
  return (
    <>
      <div className="searchMenu-loc px-30 xl:py-20 xl:px-0 js-form-dd js-liverSearch">
        <div>
          <div className="text-15 fw-500 ls-2 lh-16 d-flex justify-content-between ">
            <div>Location</div>

            <UncontrolledDropdown tag="div">
              <DropdownToggle tag="div" className="ml-5 text-blue-1 fw-500   sm:d-none md:d-none">
                Japan map
              </DropdownToggle>
              <DropdownMenu tag="div" className="search-box-transition nta-map-popover shadow-2" placement="bottom">
                <div>
                  <Map onSelectLocation={onSelectLocation} />
                </div>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
          <Dropdown isOpen={dropdownOpen} toggle={toggle} tag="div" className="text-15 text-light-1 ls-2 lh-16 location-search">
            <DropdownToggle tag="div" className="w-100">
              <input
                ref={locationRef}
                autoComplete="off"
                type="search"
                placeholder="City, hotel, place to go"
                className="js-search js-dd-focus"
                onChange={() => {
                  console.log(locationRef.current.value);
                  setSearchValue(locationRef.current.value);
                }}
              />
            </DropdownToggle>

            <DropdownMenu tag="div" className="search-box-transition shadow-2 dropdown-menu  location-search-result cursor-default">
              <div className="bg-white px-20 py-20 sm:px-0 sm:py-15 rounded-4 ">
                <div className="row x-gap-6 y-gap-10location-search-resu">
                  <div className="col-12 col-xl-5 col-lg-5 border-right">
                    {!loading ? (
                      <>
                        {!destinations?.length ? (
                          <>
                            <div className="fw-500">Recent search</div>
                            {recentSearch?.length ? (
                              <div style={{ maxHeight: 420, height: '100%', overflowX: 'clip' }} className="omega-scrollbar">
                                <ul className="y-gap-5 js-results px-5">
                                  {recentSearch.map((item, index) => (
                                    <li
                                      className={`-link d-block col-12 text-left rounded-4 mr-5 px-5 py-10 js-search-option mb-1 `}
                                      key={index}
                                      role="button"
                                      onClick={() => onSelectLocation(item.name || '', item.code || '', item.type || LocationType.Area)}
                                    >
                                      <div className="d-flex items-center">
                                        <FontAwesomeIcon icon={faHistory} className=" text-light-1 text-20 pt-4 " />
                                        <div className="ml-10">
                                          <div className="text-15 lh-12 fw-500 js-search-option-target">{item.name}</div>
                                          <div className="text-14 lh-12 text-light-1 mt-5">
                                            <span>{LocationType.__getValue(item.type)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <div className="text-light-1 d-flex items-center w-100 h-100 justify-center">No recent searches</div>
                            )}
                          </>
                        ) : null}
                      </>
                    ) : null}

                    <Spin loading={loading}>
                      <div style={{ maxHeight: 420, height: '100%', overflowX: 'clip' }} className="omega-scrollbar">
                        <ul className="y-gap-5 js-results px-5">
                          {destinations.map(item => (
                            <li
                              className={`-link d-block col-12 text-left rounded-4 mr-5 px-5 py-10 js-search-option mb-1 `}
                              key={item.code}
                              role="button"
                              onClick={() => onSelectLocation(item.name || '', item.code || '', item.type || LocationType.Area)}
                            >
                              <div className="d-flex">
                                <div
                                  className={`${
                                    item.type === LocationType.Hotel ? 'ficon-bed-property' : 'ficon-map-pin-fat'
                                  } ficon text-light-1 text-20 pt-4`}
                                />
                                <div className="ml-10">
                                  <div className="text-15 lh-12 fw-500 js-search-option-target">{item.name}</div>
                                  <div className="text-14 lh-12 text-light-1 mt-5">
                                    <span>{item.type === LocationType.Hotel ? item.hotelAddress : LocationType.__getValue(item.type)}</span>
                                  </div>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Spin>
                  </div>
                  <div className="sm:d-none lg:d-none col-xl-7 col-lg-7 popular-definition">
                    <div className="text-16 fw-500 ls-2 text-blue-1">Popular Destinations</div>
                    <div className="row x-gap-6 y-gap-6 px-10">
                      {PopularDefinition.map(location => {
                        return (
                          <div
                            key={location.code}
                            className="item col-3 d-flex flex-column items-center cursor-pointer"
                            onClick={() => {
                              let length = location.code.length;
                              let type = length === 2 ? LocationType.Prefecture : length === 3 ? LocationType.Area : LocationType.City;
                              onSelectLocation(location.title || '', location.code || '', type);
                            }}
                          >
                            <img src={location.img} alt={location.title} />
                            <div className="mt-5 text-truncate text-15  fw-500 ">{location.title}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
    </>
  );
}
